// power/use-power.jsx — the power model for the panel, live.
//
// Price, flows and today's estimate are plain entities in hass.states.
// Past days are long-term statistics (Amber's settled figures, imported
// nightly as amber:*), fetched with recorder/statistics_during_period:
// once when asked for, then hourly, since they change once a day.

import * as React from 'react';
import {
  STATS, readPrice, readSlots, advise, readNow, readToday,
  dailyFromStats, totals, standoutDay, typicalDay, localDate,
} from './model.js';
import { readLaundry } from './laundry.js';

export const HISTORY_DAYS = 30;
const REFRESH_MS = 60 * 60 * 1000;

// Past days, only while `enabled` (the page is open). null while loading,
// { days: [] } when the statistics are missing.
export function usePowerHistory(hass, enabled) {
  const conn = hass?.connection;
  const [history, setHistory] = React.useState(null);

  React.useEffect(() => {
    if (!enabled || !conn?.sendMessagePromise) return undefined;
    let cancelled = false;
    const load = () => {
      const end = new Date();
      end.setHours(0, 0, 0, 0);                       // up to the end of yesterday
      const start = new Date(end);
      start.setDate(start.getDate() - (HISTORY_DAYS + 1));
      conn.sendMessagePromise({
        type: 'recorder/statistics_during_period',
        start_time: start.toISOString(),
        end_time: end.toISOString(),
        statistic_ids: Object.values(STATS),
        period: 'day',
        types: ['change'],
        units: { energy: 'kWh' },
      }).then((result) => {
        if (cancelled) return;
        const days = dailyFromStats(result, localDate(end)).slice(-HISTORY_DAYS);
        setHistory({
          days,
          totals: totals(days),
          yesterday: days.length && days[days.length - 1].date === localDate(new Date(end.getTime() - 1)) ? days[days.length - 1] : null,
          standout: standoutDay(days),
          typical: typicalDay(days),
        });
      }).catch(() => { if (!cancelled) setHistory({ days: [], totals: null }); });
    };
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, [conn, enabled]);

  return history;
}

export function usePower(hass, now) {
  const states = hass?.states;
  return React.useMemo(() => {
    const price = readPrice(states);
    const slots = readSlots(states, now);
    const advice = advise(price, slots);
    return {
      price,
      slots,
      advice,
      laundry: readLaundry(states, advice?.spikeEnd),
      now: readNow(states),
      today: readToday(states),
      ready: !!price,
    };
  }, [states, now]);
}
